<template>
  <div>
    <PageHeader>
      <div class="tw-mb-6 tw-space-x-6">
        <InertiaLink
          href="/search"
          class="tw-display-2 tw-transition-colors hover:tw-text-current"
        >
          Mentors
        </InertiaLink>
        <InertiaLink
          href="/collections"
          class="tw-display-2 tw-text-neutral-400 tw-transition-colors hover:tw-text-neutral-500"
        >
          Collections
        </InertiaLink>
      </div>
      <div class="tw-rounded-xl tw-bg-neutral-50">
        <div class="tw-p-4">
          <div class="tw-flex tw-items-center">
            <SearchIcon class="tw-h-7 tw-w-7 tw-text-neutral-700" />
            <input
              v-model="searchInput"
              class="tw-border-0 tw-bg-transparent tw-px-3 tw-text-base placeholder:tw-text-base focus:tw-bg-transparent focus:tw-outline-none focus:tw-ring-0"
              placeholder="Search by name or keyword"
              type="text"
            />
          </div>
        </div>
        <hr class="tw-h-px tw-border-0 tw-bg-neutral-200" />
        <div
          class="tw-flex tw-items-center tw-justify-between tw-p-4 md:tw-hidden"
        >
          <BaseButton
            @click="toggleFiltersSection"
            color="outline-gray"
            size="small"
          >
            <BaseIcon name="sort-tool-2" class="tw-mr-2" />
            {{ isFiltersSectionShown ? 'Hide' : 'Show' }} filters
            <template v-if="activeFiltersCount > 0">
              <span class="tw-ml-2">•</span>
              <span class="tw-ml-2">
                {{ activeFiltersCount }}
              </span>
            </template>
          </BaseButton>
          <ClearFiltersButton
            v-if="isAnyFilterActive"
            @click="handleClearFiltersButtonClick"
            is-outline
            is-rounded
          />
        </div>
        <div>
          <BaseAccordionAnimation
            :is-open="isFiltersSectionShown || screenWidth > tailwindScreens.md"
          >
            <StickyElement
              visible-on-direction="disabled"
              stick-mode="element-end"
              stuck-class="tw-hidden md:tw-flex vue-sticky-element--stuck !tw-rounded-none tw-justify-center tw-inset-x-0 tw-bg-white tw-shadow !tw-z-[60]"
            >
              <div
                class="tw-w-full tw-flex-wrap tw-items-center tw-gap-3 tw-space-y-3 tw-overflow-hidden tw-p-4 md:tw-flex md:tw-space-y-0"
              >
                <FiltersButton
                  state-key="specialties"
                  searchable
                  @change="applyFilters"
                >
                  Skills
                </FiltersButton>
                <FiltersButton
                  state-key="software_specialties"
                  searchable
                  has-groups
                  @change="applyFilters"
                >
                  Software
                </FiltersButton>
                <FiltersButton
                  state-key="industry_specialties"
                  searchable
                  has-groups
                  @change="applyFilters"
                >
                  Industry
                </FiltersButton>
                <FiltersButton
                  state-key="price"
                  is-radio-select
                  @change="applyFilters"
                >
                  Price
                </FiltersButton>
                <FiltersButton state-key="availability" @change="applyFilters">
                  Sessions Blocks
                </FiltersButton>
                <FiltersButton state-key="hours" @change="applyFilters">
                  Times Available
                </FiltersButton>
                <FiltersButton state-key="days" @change="applyFilters">
                  Days Available
                </FiltersButton>
                <FiltersButton
                  state-key="languages"
                  searchable
                  placement="bottom-end"
                  @change="applyFilters"
                >
                  Language
                </FiltersButton>
                <FiltersButton
                  state-key="living_in_country"
                  searchable
                  placement="bottom-end"
                  @change="applyFilters"
                >
                  Country
                </FiltersButton>
                <FiltersButton
                  state-key="living_in_city"
                  searchable
                  placement="bottom-end"
                  @change="applyFilters"
                >
                  City
                </FiltersButton>
                <FiltersButton
                  state-key="companies"
                  searchable
                  placement="bottom-end"
                  @change="applyFilters"
                  :is-locked="!isUserLoggedIn"
                >
                  Company
                </FiltersButton>
                <div v-show="isAnyFilterActive" class="tw-hidden md:tw-block">
                  <IconButton
                    @click="handleClearFiltersButtonClick"
                    class="tw-bg-white"
                  >
                    <BaseIcon name="e-remove-1" />
                  </IconButton>
                </div>
              </div>
            </StickyElement>
          </BaseAccordionAnimation>
        </div>
      </div>
    </PageHeader>
    <Container>
      <div class="tw-mt-6">
        <CollectionsCarousel />
      </div>
      <div v-if="OPENAI_MATCHING_ENABLED" class="tw-mt-6">
        <MentorsMatchingCardV2 v-if="MENTORS_MATCHING_V2_ENABLED" />
        <MentorsMatchingCard v-else />
      </div>
      <div class="tw-mt-6 tw-flex tw-items-center tw-justify-between">
        <div
          class="tw-text-sm tw-font-semibold tw-text-neutral-400"
          :class="{ 'tw-invisible': !mentors.length && isLoading }"
        >
          {{ totalMentors }} mentors found
        </div>
        <SortByButton placement="bottom-end" @change="applySorting" />
      </div>
      <div v-if="isError" class="tw-mx-auto tw-mt-16 tw-w-max">
        <Alert type="error">
          <template #title>Error occurred</template>
        </Alert>
      </div>
      <transition v-else name="fade" mode="out-in" duration="100">
        <LoadingCircle
          class="tw-mx-auto tw-mt-8"
          v-if="!mentors.length && isLoading"
        />

        <div class="tw-pt-6" v-else>
          <div v-if="mentors.length" class="tw-flex tw-flex-col tw-space-y-4">
            <Mentor
              v-for="(mentor, index) in mentors"
              :key="mentor.id"
              :mentor="mentor.attributes"
              :is-blurred="!isUserLoggedIn && index >= 9"
              :mentor-added-to-lists="mentorsAddedToLists[mentor.id]"
              is-expertise-section-shown
              :is-loading="mentor.isLoading"
            >
              <template #bottom>
                <SearchTestimonialCard
                  v-if="index === 2 && isTestimonialShown"
                  class="tw-mt-4"
                />
              </template>
            </Mentor>
          </div>

          <div
            v-else
            class="tw-rounded-md tw-bg-neutral-100 tw-p-6 tw-text-center"
          >
            <h2 class="tw-heading-2 tw-text-neutral-500">
              Found no mentors matching your criteria. Try to change filters.
            </h2>
            <BaseButton
              @click="clearFilters"
              color="outline-gray"
              class="tw-mt-4"
            >
              <CloseIcon class="tw-mr-2 tw-h-3 tw-w-3" />
              Clear all filters
            </BaseButton>
          </div>

          <div
            v-if="isUserLoggedIn && canLoadMoreMentors"
            class="tw-mt-8 tw-text-center"
          >
            <BaseButton
              color="outline-gray"
              @click="loadMentors"
              :is-disabled="isLoading"
            >
              {{ isLoading ? 'Loading more mentors...' : 'Load more mentors' }}
            </BaseButton>
          </div>
        </div>
      </transition>
    </Container>

    <UnlockFullAccessModal v-if="!isUserLoggedIn" />
  </div>
</template>

<script setup>
import StickyElement from 'vue-sticky-element/vue'
import PageHeader from '@/shared/components/PageHeader.vue'
import Container from '@/shared/components/Container.vue'
import Mentor from '@/modules/growthmentor/mentors/components/Mentor.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import FiltersButton from '@/shared/components/filters/FiltersButton.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import ClearFiltersButton from '@/shared/components/filters/ClearFiltersButton.vue'
import SortByButton from '@/shared/components/SortByButton.vue'
import Alert from '@/shared/components/Alert.vue'
import SearchIcon from '@/assets/images/icons/search.svg'
import UnlockFullAccessModal from '@/shared/components/modals/UnlockFullAccessModal.vue'
import SearchTestimonialCard from '@/modules/growthmentor/mentors/components/SearchTestimonialCard.vue'
import MentorsMatchingCard from '@/modules/growthmentor/matches/components/MentorsMatchingCard.vue'
import MentorsMatchingCardV2 from '@/modules/growthmentor/matches-v2/components/MentorsMatchingCard.vue'
import CloseIcon from '@/assets/images/icons/close.svg'
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useFiltersProvider } from '@/shared/composables/filters/useFiltersProvider.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { queryParameters } from '@/shared/utils/queryParameters.js'
import axios from 'axios'
import _omit from 'lodash/omit.js'
import { ACCESS_TYPE, ROLE } from '@/shared/data/permissionSystem.js'
import _debounce from 'lodash/debounce.js'
import { useSortingProvider } from '@/shared/composables/sorting/useSortingProvider.js'
import BaseAccordionAnimation from '@/shared/components/BaseAccordionAnimation.vue'
import { useScreenWidth } from '@/shared/composables/useScreenWidth.js'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import { useFeatureFlags } from '@/shared/composables/useFeatureFlags.js'
import IconButton from '@/shared/components/IconButton.vue'
import CollectionsCarousel from '@/modules/growthmentor/collections/components/CollectionsCarousel.vue'

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
  searchSize: {
    type: Number,
    required: true,
  },
  mentorsAddedToLists: {
    type: Object,
    required: true,
  },
  testimonial: {
    type: [Object, null],
    default: null,
  },
})

const { hasAccess, hasRole, isUserLoggedIn } = usePermissionSystem()
const {
  OPENAI_MATCHING_ENABLED,
  COACHING_ENABLED,
  MENTORS_MATCHING_V2_ENABLED,
} = useFeatureFlags()
const { screenWidth, tailwindScreens } = useScreenWidth()

const isFiltersSectionShown = ref(false)
const totalMentors = ref(0)
const nextOffset = ref(0)
const mentors = ref([])
const isLoading = ref(false)
const isError = ref(false)
const mappedFilters = ref({
  specialties: props.filters.specialties,
  industry_specialties: props.filters.industrySpecialties,
  living_in_country: props.filters.countries,
  living_in_city: props.filters.livingInCities,
  price: [
    { label: 'free', value: '0' },
    { label: '< $24', value: '25' },
    { label: '$25 - $49', value: '50' },
    { label: '$50 - $99', value: '100' },
    { label: '$100 +', value: '101' },
  ],
  availability: [
    { label: '60 min', value: 'full_course' },
    { label: '30 min', value: 'half_course' },
    { label: '15 min', value: 'quarter_course' },
    COACHING_ENABLED.value
      ? { label: 'Monthly coaching', value: 'provides_coaching' }
      : null,
  ],
  hours: [
    { label: '12am - 2am', value: '0-2' },
    { label: '2am - 4am', value: '2-4' },
    { label: '4am - 6am', value: '4-6' },
    { label: '6am - 8am', value: '6-8' },
    { label: '8am - 10am', value: '8-10' },
    { label: '10am - 12pm', value: '10-12' },
    { label: '12pm - 2pm', value: '12-14' },
    { label: '2pm - 4pm', value: '14-16' },
    { label: '4pm - 6pm', value: '16-18' },
    { label: '6pm - 8pm', value: '18-20' },
    { label: '8pm - 10pm', value: '20-22' },
    { label: '10pm - 12am', value: '22-24' },
  ],
  days: [
    { label: 'Monday', value: 'monday' },
    { label: 'Tuesday', value: 'tuesday' },
    { label: 'Wednesday', value: 'wednesday' },
    { label: 'Thursday', value: 'thursday' },
    { label: 'Friday', value: 'friday' },
    { label: 'Saturday', value: 'saturday' },
    { label: 'Sunday', value: 'sunday' },
  ],
  languages: props.filters.languages,
  software_specialties: props.filters.softwareSpecialties,
  companies: props.filters.companies,
})

const {
  filtersState,
  isAnyFilterActive,
  clearFilters,
  loadPreselectedFilters,
  activeFilters,
  activeFiltersCount,
} = useFiltersProvider({
  initialState: {
    specialties: { value: [], option: 'must' },
    availability: { value: [], option: 'should' },
    price: { value: null, option: 'must' },
    industry_specialties: { value: [], option: 'must' },
    languages: { value: [], option: 'must' },
    companies: { value: [], option: 'must' },
    living_in_country: { value: [], option: 'must' },
    living_in_city: { value: [], option: 'must' },
    coaches: { value: null, option: 'must' },
    software_specialties: { value: [], option: 'must' },
    hours: { value: [], option: 'must' },
    days: { value: [], option: 'must' },
    text: { value: null, option: 'must' },
  },
  tracking: {
    companies: {
      name: 'Company Filter Clicked',
      valueKey: 'company_filter',
    },
    living_in_country: {
      name: 'Country Filter Clicked',
      valueKey: 'country_filter',
    },
    living_in_city: {
      name: 'City Filter Clicked',
      valueKey: 'city_filter',
    },
    availability: {
      name: 'Availability Filter Clicked',
      valueKey: 'availability_filter',
    },
    days: {
      name: 'Days Availability Filter Clicked',
      valueKey: 'days_availability_filter',
    },
    industry_specialties: {
      name: 'Industry Filter Clicked',
      valueKey: 'industry_filter',
    },
    languages: {
      name: 'Language Filter Clicked',
      valueKey: 'language_filter',
    },
    price: {
      name: 'Price Filter Clicked',
      valueKey: 'price_filter',
    },
    software_specialties: {
      name: 'Software Filter Clicked',
      valueKey: 'software_filter',
    },
    specialties: {
      name: 'Expertise Filter Clicked',
      valueKey: 'expertise_filter',
    },
    hours: {
      name: 'Time Availability Filter Clicked',
      valueKey: 'time_availability_filter',
    },
  },
  availableFilters: mappedFilters,
})

const { sortingState, updateSortingState, loadPreselectedSorting } =
  useSortingProvider({
    initialState: {
      field: '_score',
      order: 'desc',
    },
    sortingOptions: [
      { label: 'Reviews', field: '_score', order: 'desc' },
      { label: 'Newest', field: 'mentor_first_live_at', order: 'desc' },
      { label: 'Next Available', field: 'next_available_time', order: 'asc' },
    ],
  })

const canLoadMoreMentors = computed(() => {
  return nextOffset.value <= totalMentors.value
})

const searchInput = computed({
  get() {
    return filtersState.value.text.value
  },
  set(newValue) {
    filtersState.value.text.value = newValue
    applySearch()
  },
})

const isTestimonialShown = computed(() => {
  if (!props.testimonial) return false
  return hasAccess(ACCESS_TYPE.NONE)
})

const loadMentors = async () => {
  if (!canLoadMoreMentors.value) return

  isLoading.value = true
  mentors.value = [
    ...mentors.value,
    // Fake mentor object to show skeleton loader
    ...Array.from({ length: props.searchSize }).map((_, index) => ({
      id: Date.now() + index,
      attributes: { id: Date.now() + index },
      isLoading: true,
    })),
  ]
  try {
    const { data } = await axios.patch('/search', {
      ...filtersState.value,
      sort: sortingState.value,
      size: props.searchSize,
      offset: nextOffset.value,
    })

    // Remove fake mentors
    mentors.value = mentors.value.filter((mentor) => !mentor.isLoading)

    mentors.value = mentors.value.concat(data.results.data)
    totalMentors.value = data.total
    nextOffset.value += props.searchSize
    isLoading.value = false
  } catch (_) {
    isError.value = true
  }
}

const applyFilters = _debounce(function () {
  reloadMentors()
  fetchFilters()
}, 500)

const applySorting = () => {
  reloadMentors()
}

const applySearch = _debounce(function () {
  reloadMentors()
  if (searchInput.value) {
    window.segmentAnalytics.track('Search Browse Mentors', {
      user_type: window.segmentAnalytics.userType(),
      search_input: searchInput.value.toLowerCase(),
    })
  }
}, 1000)

const resetLastSearchData = () => {
  mentors.value = []
  nextOffset.value = 0
}

const reloadMentors = () => {
  queryParameters().setParams({
    ...activeFilters.value,
    ...sortingState.value,
  })
  resetLastSearchData()
  loadMentors()
}

const fetchFilters = async () => {
  const params = queryParameters().getParams()
  const { data } = await axios.get('filters', {
    params: { filters: params },
  })
  mappedFilters.value.companies = data.companies
  mappedFilters.value.living_in_country = data.countries
  mappedFilters.value.living_in_city = data.livingInCities
  mappedFilters.value.industry_specialties = data.industrySpecialties
  mappedFilters.value.languages = data.languages
  mappedFilters.value.software_specialties = data.softwareSpecialties
  mappedFilters.value.specialties = data.specialties
}

const handleClearFiltersButtonClick = () => {
  clearFilters()
  applyFilters()
}

const toggleFiltersSection = () => {
  isFiltersSectionShown.value = !isFiltersSectionShown.value
}

onBeforeMount(() => {
  // Filters initialization
  let params = queryParameters().getParams()

  // Backward compatibility with URLs like `/search?specialty=Advice%20on%20funding`
  if (Object.keys(params).includes('specialty')) {
    params.specialties = [params.specialty]
    params = _omit(params, 'specialty')
  }

  // Backward compatibility with URLs like `/search?field=created_at`
  if (Object.keys(params).includes('field')) {
    params.field = params.field.replace('created_at', 'mentor_first_live_at')
  }

  // Sort mentors by newest
  if (hasAccess(ACCESS_TYPE.GROWTHMENTOR) && hasRole(ROLE.MENTEE)) {
    updateSortingState('mentor_first_live_at', 'desc')
  }

  // Sort mentors by next available for guests
  if (!isUserLoggedIn.value) {
    updateSortingState('next_available_time', 'asc')
  }

  loadPreselectedFilters(params)
  loadPreselectedSorting(params)

  loadMentors()
})

onBeforeUnmount(() => {
  clearFilters()
})
</script>
